<template>
  <div>
    <button v-if="itemId" :disabled="disabled" :class="$style.itemButton" @click="onClick">
      <MessageSvg width="29" />
      <span>質問</span>
    </button>
    <button v-else :disabled="disabled" title="メッセージを送る" aria-label="メッセージを送る" :class="$style.shopButton" @click="onClick">
      <MessageSvg />
    </button>
    <IModal v-model="isShowMessageDialog" width="600px">
      <p :class="$style.title">メッセージを送る</p>
      <Form :validation-schema="validationSchema" :class="$style.form" @submit="onSubmit">
        <section>
          <ITextField v-model="formData.subject" name="subject" title="件名" />
        </section>
        <section>
          <ITextArea v-model="formData.message" name="message" title="メッセージ" :rows="5" :max="MESSAGE_MAX" />
        </section>
        <div :class="$style.attach">
          <button @click.prevent="isShowImage = !isShowImage">画像を添付する</button>
        </div>
        <section v-if="isShowImage">
          <div :class="$style.sectionHeader">
            <p>添付画像(任意)</p>
            <IMenu width="276">
              <template #link>
                <QuestionIcon top="3" />
              </template>
              <template #menu>
                [推奨画像サイズ] 横320ピクセル以上<br />
                [対応フォーマット] GIF、JPEG、PNG<br />
                [ファイルサイズ] 10MB以下
              </template>
            </IMenu>
          </div>
          <PhotoUploader v-model="formData.pictures" name="pictures" />
        </section>
        <div :class="$style.send">
          <IButton type="primary" native-type="submit">送信する</IButton>
        </div>
      </Form>
    </IModal>
    <IModal v-model="isShowMessageSendDialog">
      <p class="mb-20">メッセージを送信しました</p>
      <IButton :href="`/conversation/${conversationId}`" type="primary" size="small" flexible center>メッセージを確認する</IButton>
    </IModal>
  </div>
</template>

<script setup lang="ts">
import { Credential } from '@iichi/domain/member';
import { toTypedSchema } from '@vee-validate/zod';
import { Form } from 'vee-validate';
import { ref, reactive } from 'vue';
import * as z from 'zod';
import { type Picture } from '@/types/photo-uploader';
import { messageSend } from '@/js/lib/api/v1/message';
import { useUserStore } from '@/js/lib/store/user';
import { confirm, info, loading } from '@/js/lib/ui';
import { signinSignup } from '@/js/lib/ui/signin-signup';
import PhotoUploader from '@/js/components/common/ui/form/PhotoUploader.vue';
import IButton from '@/js/components/molecules/IButton.vue';
import IMenu from '@/js/components/molecules/IMenu.vue';
import IModal from '@/js/components/molecules/IModal.vue';
import ITextArea from '@/js/components/molecules/ITextArea.vue';
import ITextField from '@/js/components/molecules/ITextField.vue';
import QuestionIcon from '@/js/components/organisms/QuestionIcon.vue';
import MessageSvg from '@/svgs/vue/message.svg';

interface FormData {
  subject: string;
  message: string;
  pictures: Picture[];
}

const MESSAGE_MAX = 800;
const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    itemId?: number;
    shopId: number;
    subject?: string;
  }>(),
  {
    itemId: 0,
    subject: '',
  },
);
const userStore = useUserStore();
const isShowImage = ref(false);
const isShowMessageDialog = ref(false);
const isShowMessageSendDialog = ref(false);
const conversationId = ref(0);
const formData = reactive<FormData>({
  subject: props.subject,
  message: props.itemId ? `\n\n${window.location.href.replace(window.location.search, '').replace(window.location.hash, '')}` : '',
  pictures: [],
});
const validationSchema = toTypedSchema(
  z.object({
    subject: z.string().min(1).max(200),
    message: z.string().min(1).max(MESSAGE_MAX),
  }),
);
const onClick = () => {
  if (userStore.isAuthenticated) {
    if (userStore.hasCredential(Credential.EMAIL_CONFIRMED)) {
      isShowMessageDialog.value = true;
    } else {
      info('メッセージを送信するにはメール認証が必要です。\n「マイページ > 設定 > ログイン情報 > メールアドレスの変更」よりメール認証を行ってください。');
    }
  } else {
    signinSignup({
      next: window.location.href,
      callback: () => {
        window.location.reload();
      },
    });
  }
};
const onSubmit = async () => {
  if (await confirm('メッセージを送信します。\nよろしいですか？')) {
    loading({
      asyncFunc: async () => {
        const { conversation } = await messageSend({
          shopId: props.shopId,
          itemId: props.itemId,
          picture: formData.pictures[0],
          subject: formData.subject,
          message: formData.message,
        });
        conversationId.value = conversation.conversationId;
        isShowMessageDialog.value = false;
        isShowMessageSendDialog.value = true;
      },
    });
  }
};
</script>

<style module lang="scss">
.itemButton {
  font-size: 12px;
  transition: opacity 0.3s ease-in-out;
  width: 35px;

  @media (--sp) {
    font-size: 11px;
  }

  svg {
    fill: #4b4b4b;
    margin: 5px 0 3px;
    vertical-align: bottom;
  }

  &:not(:disabled):hover {
    opacity: 0.8;
  }

  &:disabled {
    color: var(--color-gray500);
    cursor: not-allowed;

    svg {
      fill: var(--color-gray500);
    }
  }

  span {
    display: block;
  }
}

.shopButton {
  border: 1px solid var(--color-gray300);
  border-radius: 50%;
  display: flex;
  font-size: 18px;
  padding: 7px 6px;

  &:not(:disabled):hover {
    opacity: 0.8;
  }

  svg {
    fill: #4b4b4b;
    height: 18px;
    position: relative;
    top: 1px;
  }

  &:disabled {
    cursor: not-allowed;

    svg {
      fill: var(--color-gray500);
    }
  }

  span {
    @media (--sp) {
      display: none;
    }
  }
}

.title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
}

.form {
  text-align: left;

  section {
    margin-bottom: 15px;

    .sectionHeader {
      align-items: center;
      display: flex;
      margin-bottom: 6px;

      > p {
        margin: 0 8px 0 0;
      }
    }
  }

  .attach {
    margin-bottom: 20px;

    button {
      border-bottom: 1px dashed var(--color-gray600);
      display: inline-block;

      &:hover {
        text-decoration: none;
      }

      &::selection {
        background: none;
      }
    }
  }

  .send {
    padding: 15px 0 0;
    text-align: center;
  }
}
</style>
